import type { CmmdRuntimeInventoryRuntime } from "@cmmd-forge/contracts";
import { classifyEnvironmentId } from "../../environments/environmentIdClassification";
import { runtimeAvailability } from "../../cmmd/runtimeAvailabilityForEnvironment";

import {
  classifyRemoteRuntimeConnectionError,
  type RemoteRuntimeConnectionState,
  RemoteRuntimeAvailability,
} from "./remoteRuntimeThreadConnection.logic";
import type { CmmdRuntimeThreadReplicaSnapshot } from "../../cmmd/cmmdRuntimeThreadReplicaResource";
import type { RemoteRuntimeThreadViewState } from "./RemoteRuntimeThreadView";

/**
 * Map a transcript snapshot onto what the view should render.
 *
 * `forge-durable-cloud-history-proof` requires a 401/403/404 to "discard or
 * suppress stale cached data for that authorization context". The two halves
 * are split deliberately:
 *
 * - **Discard** belongs to 401/403, the genuine revocation signals, and happens
 *   upstream in `cmmdRuntimeThreadReplicaResource` — such an error arrives here
 *   with no replica and stays a hard error.
 * - **Suppress** belongs to 404 and to transient network failures, where the
 *   resource keeps the last authorized replica. Destroying it was the
 *   destructive reading: a 404 is not a revocation on this route (CMMD's access
 *   service throws and maps to 403), so it wiped a transcript the user was
 *   already reading whenever the control plane resolved the read into the wrong
 *   workspace (CMMD FM-120), and Retry re-issued the same request.
 *
 * So retained history stays on screen but is marked stale — never presented as
 * current. A snapshot carrying both a replica and an error message is stale by
 * construction; that is the only thing `staleMessage` means.
 */
export function toRemoteRuntimeThreadViewState(
  snapshot: CmmdRuntimeThreadReplicaSnapshot,
): RemoteRuntimeThreadViewState {
  if (snapshot.kind === "ready") return { kind: "ready", replica: snapshot.replica };
  if (snapshot.kind === "loading" && snapshot.replica) {
    return { kind: "ready", replica: snapshot.replica };
  }
  if (snapshot.kind === "error" && snapshot.replica) {
    return { kind: "ready", replica: snapshot.replica, staleMessage: snapshot.message };
  }
  if (snapshot.kind === "error") return { kind: "error", message: snapshot.message };
  return { kind: "loading" };
}

export interface RemoteRuntimeThreadRouteTarget {
  readonly connectionKind: "cloud" | "relay";
  readonly runtimeId: string;
  readonly runtimeDisplayName: string;
  readonly threadTitle: string;
  readonly availability: RemoteRuntimeAvailability;
  readonly historyOnly: boolean;
}

export function resolveRemoteRuntimeThreadHistorySource(
  target: RemoteRuntimeThreadRouteTarget,
): "live" | "replica" {
  return target.connectionKind === "cloud" ? "replica" : "live";
}

export function shouldUseLocalThreadSurface(
  target: RemoteRuntimeThreadRouteTarget,
  connectionState: RemoteRuntimeConnectionState,
): boolean {
  return (
    resolveRemoteRuntimeThreadHistorySource(target) === "live" || connectionState.kind === "live"
  );
}

function threadTitle(runtime: CmmdRuntimeInventoryRuntime, threadId: string): string | null {
  for (const project of runtime.projects) {
    const thread = project.threads.find((candidate) => candidate.threadId === threadId);
    if (thread) return thread.title;
  }
  return null;
}

export function resolveRemoteRuntimeThreadConnectionState(input: {
  readonly target: RemoteRuntimeThreadRouteTarget | null;
  readonly cloudThreadExists: boolean;
  readonly cloudConnection:
    | { readonly kind: "idle" | "connecting" | "connected" }
    | { readonly kind: "failed"; readonly error: unknown };
  readonly relayState: RemoteRuntimeConnectionState;
}): RemoteRuntimeConnectionState {
  if (input.target?.connectionKind !== "cloud") return input.relayState;
  if (input.cloudConnection.kind === "failed") {
    return classifyRemoteRuntimeConnectionError(input.cloudConnection.error);
  }
  if (input.target.availability === "unknown") return { kind: "checking" };
  if (input.cloudConnection.kind === "connected") {
    return input.cloudThreadExists ? { kind: "live" } : { kind: "connecting" };
  }
  if (input.target.availability === "offline") return { kind: "offline" };
  return input.cloudConnection.kind === "idle" ? { kind: "idle" } : { kind: "connecting" };
}

export function findRemoteRuntimeThreadRouteTarget(input: {
  readonly environmentId: string;
  readonly threadId: string;
  readonly currentRegisteredRuntimeId: string | null;
  readonly runtimes: readonly CmmdRuntimeInventoryRuntime[];
  readonly inventoryResolved?: boolean;
  readonly cloudRuntimeId?: string | null;
}): RemoteRuntimeThreadRouteTarget | null {
  if (input.environmentId === input.currentRegisteredRuntimeId) return null;
  const cloudRuntimeId =
    input.cloudRuntimeId ?? classifyEnvironmentId(input.environmentId).legacyRuntimeId;
  if (cloudRuntimeId !== null) {
    const runtime = input.runtimes.find(
      (candidate) =>
        candidate.runtime.runtimeKind === "cloud-vm" && candidate.runtime.id === cloudRuntimeId,
    );
    return {
      connectionKind: "cloud",
      runtimeId: cloudRuntimeId,
      runtimeDisplayName: runtime?.runtime.displayName ?? "Forge Cloud",
      threadTitle: runtime
        ? (threadTitle(runtime, input.threadId) ?? "Conversation")
        : "Conversation",
      availability: runtime
        ? runtimeAvailability(runtime)
        : input.inventoryResolved === true
          ? "offline"
          : "unknown",
      historyOnly: input.inventoryResolved === true && runtime === undefined,
    };
  }
  const runtime = input.runtimes.find(
    (candidate) =>
      candidate.runtime.runtimeKind === "desktop-local" &&
      candidate.runtime.id === input.environmentId,
  );
  if (!runtime) return null;
  const title = threadTitle(runtime, input.threadId);
  return title
    ? {
        connectionKind: "relay",
        runtimeId: runtime.runtime.id,
        runtimeDisplayName: runtime.runtime.displayName,
        threadTitle: title,
        availability: runtimeAvailability(runtime),
        historyOnly: false,
      }
    : null;
}
